import {
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import {
  getLinkQueryParams,
  isEmptyString,
  parseErrorMessage,
} from 'utils/helper';
import { communiqueAction } from 'reduxStateSlice/communique';
import { organizationMediaAction } from 'reduxStateSlice/organizationMedia';
import { appStateSettingsAction } from 'reduxStateSlice/appState';
import { ModuleTypes } from 'types/appState';
import { auth0M2MAccessToken } from 'env';
import { hasToken, setM2MHeaders } from 'utils/auth';
import { useGetAppointmentCommuniqueByToken, useGetOrgBannerPictureByPatientId } from 'hooks/useApi';

const useDisclaimerCommunique = () => {
  const { mutateAsync: getOrgBannerPictureByPatientId } = useGetOrgBannerPictureByPatientId();
  const router = useRouter();
  const { token } = getLinkQueryParams(router.query);
  const [errorMessage, setErrorMessage] = useState('');
  const [m2mReady, setM2mReady] = useState(false);
  const [isPreparingCommunique, setIsPreparingCommunique] = useState(true);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!router.isReady) return;

    // Ensure the M2M token header is set on `axiosM2MInstance` before fetching
    if (!hasToken()) {
      setM2MHeaders(auth0M2MAccessToken ?? '');
    }
    setM2mReady(hasToken());

    // Link is invalid only when the token itself is missing
    if (isEmptyString(token)) {
      setErrorMessage('Invalid or broken link');
      setIsPreparingCommunique(false);
    } else {
      setErrorMessage('');
      setIsPreparingCommunique(true);
    }
  }, [router.isReady, token, auth0M2MAccessToken]);

  const enabled = router.isReady && !isEmptyString(token) && m2mReady;
  const {
    data: appointmentCommunique,
    error,
    isFetching,
  } = useGetAppointmentCommuniqueByToken({ token: token ?? '' }, enabled);

  const handleAppointmentCommunique = useCallback(async () => {
    // If the request failed (e.g. 401), don't proceed to next screen using cached/stale data.
    if (error) return;
    if (!appointmentCommunique) return;

    const getMediaList = await getOrgBannerPictureByPatientId(
      appointmentCommunique?.patientId ?? 0,
    );

    dispatch(organizationMediaAction(getMediaList?.list ?? []));
    dispatch(communiqueAction(appointmentCommunique));
    if (appointmentCommunique?.sendConfirmation) {
      dispatch(
        appStateSettingsAction({ name: ModuleTypes.appointmentConfirmation, page: null }),
      );
    }
    setErrorMessage('');
    setIsPreparingCommunique(false);
  }, [appointmentCommunique, dispatch, error, getOrgBannerPictureByPatientId]);

  useEffect(() => {
    if (!appointmentCommunique) return;
    if (error) return;

    handleAppointmentCommunique().catch((e) => {
      setErrorMessage(parseErrorMessage(e));
      setIsPreparingCommunique(false);
    });
  }, [appointmentCommunique, error, handleAppointmentCommunique]);

  useEffect(() => {
    if (!error) return;
    setErrorMessage(parseErrorMessage(error));
    setIsPreparingCommunique(false);
  }, [error]);

  const isLoading = !router.isReady || isPreparingCommunique || (enabled && isFetching);
  return { errorMessage, isLoading };
};

export default useDisclaimerCommunique;
